import React, { useRef, useState } from 'react';
import { HBox } from '../layout';
import type { CommonInputProps } from './CommonInputProps';
import { CropperModal } from './internal';
import { PersonImage, Tool } from '../basic';
import { intl, notifyConfirm } from '../../lib';
import { isEmptyString } from '../../util';
import type { ClassNameProp } from '../common-types';

/**
 * Props for a {@link ImageInput}.
 */
export type ImageInputProps = Required<
  Pick<CommonInputProps<string>, 'onChange' | 'value'>
> &
  ClassNameProp &
  Pick<CommonInputProps<string>, 'disabled'>;

/**
 * Input field for a images. This is meant to be used for things like a user's profile picture.
 * A modal element for cropping the image will show up after selecting an image to use.
 *
 * @param properties {@link ImageInputProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function ImageInput({
  value,
  onChange,
  disabled = false,
  className = '',
  ...props
}: Readonly<ImageInputProps>): JSX.Element {
  const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cssClasses = ['ehs-image-input-field'];
  if (disabled) cssClasses.push('disabled');
  if (!isEmptyString(className)) cssClasses.push(className);

  return (
    <HBox {...props} className={cssClasses.join(' ')} aria-disabled={disabled}>
      <button
        className="image-input-button"
        type="button"
        onClick={() => {
          fileInputRef.current?.click();
        }}
        disabled={disabled}
      >
        <PersonImage
          className="image-input-image"
          emptyState="defaultAvatar"
          image={value}
        />
        <span className="upload-new-text">
          {intl('view.patient.upload.new')}
        </span>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          ref={fileInputRef}
          onChange={event => {
            if (objectUrl) {
              window.URL.revokeObjectURL(objectUrl);
            }
            setObjectUrl(URL.createObjectURL(event.target.files?.[0]));
          }}
        />
      </button>
      <Tool
        className="delete-tool"
        icon="delete"
        tooltip={intl('view.button.clear')}
        disabled={isEmptyString(value) || disabled}
        onClick={() => {
          notifyConfirm({
            title: intl('view.button.delete'),
            message: intl('view.prompt.profileimage.delete'),
            onConfirm: () => onChange('')
          });
        }}
      />
      {objectUrl && (
        <CropperModal
          image={objectUrl}
          onSave={croppedImage => {
            setObjectUrl(undefined);
            if (fileInputRef.current) fileInputRef.current.value = '';
            onChange(croppedImage.substring(croppedImage.indexOf(',') + 1));
          }}
          onCancel={() => {
            setObjectUrl(undefined);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
      )}
    </HBox>
  );
}
